import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import LandingScreen from "@/components/valuation/LandingScreen";
import LandingScreenAlt from "@/components/valuation/LandingScreenAlt";
import QuestionnaireWizard from "@/components/valuation/QuestionnaireWizard";
import ProcessingScreen from "@/components/valuation/ProcessingScreen";
import LeadCaptureScreen from "@/components/valuation/LeadCaptureScreen";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Questions configuration
const questions = [
    // Operational Questions (easier first to warm up users)
    {
        id: "coffee_kg_week",
        section: "Operational Metrics",
        question: "How many kilograms of coffee beans does your café use per week?",
        type: "number",
        placeholder: "e.g. 15",
        helperText: "Estimate based on your average weekly orders",
        suffix: "kg"
    },
    {
        id: "coffee_revenue_percentage",
        section: "Operational Metrics",
        question: "What percentage of your revenue comes from coffee?",
        type: "select",
        options: ["<40%", "40–60%", "60%", "80% plus", "100% Coffee"]
    },
    {
        id: "staff_count",
        section: "Operational Metrics",
        question: "How many staff are required to operate the café (including yourself)?",
        type: "number",
        placeholder: "e.g. 5",
        helperText: "Total number of staff needed to run daily operations",
        suffix: "staff"
    },
    {
        id: "seating_capacity",
        section: "Operational Metrics",
        question: "How many seats does your café have?",
        type: "select",
        options: ["<20", "20–40", "40–80", "80–120", "120+"]
    },
    {
        id: "customers_per_day",
        section: "Operational Metrics",
        question: "Approximately how many customers do you serve per day?",
        type: "number",
        placeholder: "e.g. 150",
        helperText: "Average daily customer count",
        suffix: "customers"
    },
    // Financial Questions
    {
        id: "annual_revenue",
        section: "Financial Inputs",
        question: "What was your total revenue last year?",
        type: "currency",
        placeholder: "e.g. 550000",
        helperText: "Total sales/turnover before any expenses"
    },
    {
        id: "net_profit",
        section: "Financial Inputs",
        question: "What was your net profit before tax?",
        type: "currency",
        placeholder: "e.g. 80000",
        helperText: "Profit after all expenses (enter 0 for break-even, negative for loss)"
    },
    {
        id: "owner_wages",
        section: "Financial Inputs",
        question: "How much do you pay yourself per year from the business?",
        type: "currency",
        placeholder: "e.g. 60000",
        helperText: "Only include this if your wages were recorded as an expense (not if taken from profits). Enter 0 if not applicable."
    },
    {
        id: "owner_hours",
        section: "Financial Inputs",
        question: "How many hours per week do you work in the business?",
        type: "number",
        placeholder: "e.g. 40",
        helperText: "Average hours you spend working in the café each week",
        suffix: "hours"
    },
    // Business Profile Questions
    {
        id: "years_owned",
        section: "Business Stability",
        question: "How many years have you owned the café?",
        type: "select",
        options: ["<1 year", "1–3 years", "3–6 years", "6–10 years", "10+"]
    },
    {
        id: "days_open",
        section: "Business Stability",
        question: "How many days per week does your business operate?",
        type: "select",
        options: ["1 day", "2 days", "3 days", "4 days", "5 days", "6 days", "7 days"]
    },
    {
        id: "fitout_value",
        section: "Asset Value",
        question: "What is the approximate replacement value of your café fit-out and equipment?",
        type: "select",
        options: ["<$50k", "$50k–$100k", "$100k–$200k", "$200k–$350k", "$350k+"]
    }
];

const ValuationTool = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const variant = searchParams.get("v") || "1"; // Default to version 1
    
    const [stage, setStage] = useState("landing"); // landing, questionnaire, processing, lead-capture
    const [answers, setAnswers] = useState({});
    const [leadInfo, setLeadInfo] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Restore answers from sessionStorage on mount
    useEffect(() => {
        const savedAnswers = sessionStorage.getItem("cafeValuationAnswers");
        if (savedAnswers) {
            setAnswers(JSON.parse(savedAnswers));
        }
    }, []);

    // Save answers to sessionStorage whenever they change
    useEffect(() => {
        if (Object.keys(answers).length > 0) {
            sessionStorage.setItem("cafeValuationAnswers", JSON.stringify(answers));
        }
    }, [answers]);

    const handleStartValuation = () => {
        setStage("questionnaire");
    };

    const handleQuestionnaireComplete = (completedAnswers) => {
        setAnswers(completedAnswers);
        setStage("processing");
        
        // Show processing for 3 seconds before lead capture
        setTimeout(() => {
            setStage("lead-capture");
        }, 3000);
    };

    const handleLeadSubmit = async (leadData) => {
        setLeadInfo(leadData);
        setIsSubmitting(true);
        
        try {
            const response = await axios.post(`${API}/valuation/calculate`, {
                questionnaire: answers,
                lead: leadData
            });
            
            // Store results in sessionStorage for the results page
            sessionStorage.setItem("valuationResult", JSON.stringify(response.data));
            sessionStorage.setItem("leadInfo", JSON.stringify(leadData));
            sessionStorage.removeItem("cafeValuationAnswers"); // Clear saved answers
            
            // Navigate to /results page (for Google tracking)
            navigate("/results");
        } catch (error) {
            console.error("Valuation error:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const pageVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 }
    };

    return (
        <div className="min-h-screen" data-testid="valuation-tool">
            <AnimatePresence mode="wait">
                {stage === "landing" && (
                    <motion.div
                        key="landing"
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.4 }}
                    >
                        {variant === "2" ? (
                            <LandingScreenAlt onStart={handleStartValuation} />
                        ) : (
                            <LandingScreen onStart={handleStartValuation} />
                        )}
                    </motion.div>
                )}

                {stage === "questionnaire" && (
                    <motion.div
                        key="questionnaire"
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.4 }}
                    >
                        <QuestionnaireWizard
                            questions={questions}
                            initialAnswers={answers}
                            onComplete={handleQuestionnaireComplete}
                            onBack={() => setStage("landing")}
                        />
                    </motion.div>
                )}

                {stage === "processing" && (
                    <motion.div
                        key="processing"
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.4 }}
                    >
                        <ProcessingScreen />
                    </motion.div>
                )}

                {stage === "lead-capture" && (
                    <motion.div
                        key="lead-capture"
                        variants={pageVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.4 }}
                    >
                        <LeadCaptureScreen 
                            onSubmit={handleLeadSubmit} 
                            isSubmitting={isSubmitting}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ValuationTool;
